import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ChartBarIcon,
  UsersIcon,
  BookOpenIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  EyeIcon,
  AcademicCapIcon,
} from '@heroicons/react/24/outline';
import apiService from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import PageHeader from '../components/PageHeader';

const StudentProgress = () => {
  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [batchStudents, setBatchStudents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBatches();
  }, []);

  const fetchBatches = async () => {
    try {
      const data = await apiService.getMyBatches();
      // Handle different response formats
      if (Array.isArray(data)) {
        setBatches(data);
      } else if (data && data.results && Array.isArray(data.results)) {
        setBatches(data.results);
      } else if (data && data.data && Array.isArray(data.data)) {
        setBatches(data.data);
      } else {
        console.warn('Unexpected batches data format:', data);
        setBatches([]);
      }
    } catch (error) {
      console.error('Failed to fetch batches:', error);
      setBatches([]);
    } finally {
      setLoading(false);
    }
  };

  const handleBatchSelect = async (batch) => {
    try {
      const students = await apiService.getBatchStudents(batch.id);
      setBatchStudents(students);
      setSelectedBatch(batch);
    } catch (error) {
      console.error('Failed to fetch batch students:', error);
    }
  };

  const getProgressColor = (progress) => {
    if (progress >= 80) return 'text-green-600';
    if (progress >= 60) return 'text-yellow-600';
    if (progress >= 40) return 'text-orange-600';
    return 'text-red-600';
  };

  const getProgressBarColor = (progress) => {
    if (progress >= 80) return 'bg-green-600';
    if (progress >= 60) return 'bg-yellow-600';
    if (progress >= 40) return 'bg-orange-600';
    return 'bg-red-600';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="large" message="Loading progress data..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Student Progress"
        subtitle="Monitor and track student performance across your courses"
      />

      {/* Batch Selection */}
      <div className="card">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Select Batch</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {batches.map((batch) => (
            <button
              key={batch.id}
              onClick={() => handleBatchSelect(batch)}
              className={`p-4 border rounded-lg text-left hover:shadow-md transition-shadow duration-200 ${
                selectedBatch?.id === batch.id
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="h-8 w-8 rounded-lg bg-primary-100 flex items-center justify-center">
                    <BookOpenIcon className="h-5 w-5 text-primary-600" />
                  </div>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-semibold text-gray-900">{batch.name}</h3>
                  <p className="text-xs text-gray-500">{batch.course?.name || batch.course?.title || 'No Course'}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
        
        {batches.length === 0 && (
          <div className="text-center py-8">
            <BookOpenIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-lg font-semibold text-gray-900">No batches assigned</h3>
            <p className="mt-1 text-sm text-gray-500">
              You haven't been assigned to any batches yet. Contact your administrator to get assigned to batches.
            </p>
          </div>
        )}
      </div>

      {/* Student Progress Table */}
      {selectedBatch && (
        <div className="card">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Progress for {selectedBatch.name}
              </h3>
              <span className="text-sm text-gray-500">
                {batchStudents.length} students
              </span>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Student
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Course Progress
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Assignments
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Attendance
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {batchStudents.map((student) => (
                    <tr key={student.id} className="hover:bg-gray-50">
                                              <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                                <span className="text-sm font-medium text-primary-600">
                                  {student.student_detail?.full_name?.[0] || student.student_name?.[0] || 'S'}
                                </span>
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {student.student_detail?.full_name || student.student_name || 'Student Name'}
                              </div>
                              <div className="text-sm text-gray-500">{student.student_detail?.email || student.email || 'No email'}</div>
                            </div>
                          </div>
                        </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-full bg-gray-200 rounded-full h-2 mr-2">
                            <div
                              className={`h-2 rounded-full ${getProgressBarColor(student.progress || 0)}`}
                              style={{ width: `${student.progress || 0}%` }}
                            ></div>
                          </div>
                          <span className={`text-sm font-medium ${getProgressColor(student.progress || 0)}`}>
                            {student.progress || 0}%
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-900">
                          <CheckCircleIcon className="h-4 w-4 text-green-500 mr-1" />
                          {student.completed_assignments || 0} / {student.total_assignments || 0}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-900">
                          <ClockIcon className="h-4 w-4 text-blue-500 mr-1" />
                          {student.attendance_rate || 0}%
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          student.status === 'active' ? 'bg-green-100 text-green-800' :
                          student.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                          student.status === 'at_risk' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {student.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <Link
                          to={`/progress/${student.id}/${selectedBatch.course?.id}`}
                          className="text-primary-600 hover:text-primary-500 mr-3"
                        >
                          <ChartBarIcon className="h-4 w-4 inline" />
                        </Link>
                        <Link
                          to={`/students/${student.id}`}
                          className="text-primary-600 hover:text-primary-500"
                        >
                          <EyeIcon className="h-4 w-4 inline" />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {batchStudents.length === 0 && (
              <div className="text-center py-8">
                <UsersIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No students in this batch</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Students will appear here once they are enrolled and start making progress.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Progress Summary */}
      {selectedBatch && batchStudents.length > 0 && (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="inline-flex items-center justify-center p-3 rounded-md bg-green-500 text-white">
                    <AcademicCapIcon className="h-6 w-6" />
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Average Progress
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {Math.round(batchStudents.reduce((acc, student) => acc + (student.progress || 0), 0) / batchStudents.length)}%
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="inline-flex items-center justify-center p-3 rounded-md bg-blue-500 text-white">
                    <CheckCircleIcon className="h-6 w-6" />
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Active Students
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {batchStudents.filter(s => s.status === 'active').length}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="inline-flex items-center justify-center p-3 rounded-md bg-yellow-500 text-white">
                    <ClockIcon className="h-6 w-6" />
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      At Risk
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {batchStudents.filter(s => s.status === 'at_risk').length}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="inline-flex items-center justify-center p-3 rounded-md bg-purple-500 text-white">
                    <XCircleIcon className="h-6 w-6" />
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Completed
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {batchStudents.filter(s => s.status === 'completed').length}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {!selectedBatch && batches.length > 0 && (
        <div className="text-center py-12">
          <ChartBarIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Select a batch to view progress</h3>
          <p className="mt-1 text-sm text-gray-500">
            Choose a batch from above to see detailed student progress information.
          </p>
        </div>
      )}
    </div>
  );
};

export default StudentProgress; 